import { createRouter, createWebHistory, RouteRecordRaw, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import { appScrollBehavior } from './scrollBehavior';
import { isRouteAllowed, requirementOf, safeRedirect, signInRouteName } from '../rbac/guard';
import { SETTINGS_PERMISSION } from '../rbac/navigation';

// Layouts
const AuthLayout = () => import('../layouts/AuthLayout.vue');
const DashboardLayout = () => import('../layouts/DashboardLayout.vue');
const AdminLayout = () => import('../layouts/AdminLayout.vue');
const ClientLayout = () => import('../layouts/ClientLayout.vue');

// Auth pages
const SignIn = () => import('../pages/auth/SignIn.vue');
const Login = () => import('../pages/auth/Login.vue');
const Register = () => import('../pages/auth/Register.vue');
const ForgotPassword = () => import('../pages/auth/ForgotPassword.vue');
const ResetPassword = () => import('../pages/auth/ResetPassword.vue');
const VerifyEmail = () => import('../pages/auth/VerifyEmail.vue');
const SuperAdminLogin = () => import('../pages/auth/SuperAdminLogin.vue');
const ClientLogin = () => import('../pages/auth/ClientLogin.vue');

// Company Dashboard pages
const Dashboard = () => import('../pages/dashboard/Dashboard.vue');
const AdvancedDashboard = () => import('../pages/dashboard/AdvancedDashboard.vue');
const Projects = () => import('../pages/dashboard/projects/Projects.vue');
const ProjectDetail = () => import('../pages/dashboard/projects/ProjectDetail.vue');
const Tasks = () => import('../pages/dashboard/tasks/Tasks.vue');
const TaskDetail = () => import('../pages/dashboard/tasks/TaskDetail.vue');
const Employees = () => import('../pages/dashboard/hr/Employees.vue');
const EmployeeDetail = () => import('../pages/dashboard/hr/EmployeeDetail.vue');
const Attendance = () => import('../pages/dashboard/hr/Attendance.vue');
const Leaves = () => import('../pages/dashboard/hr/Leaves.vue');
const Shifts = () => import('../pages/dashboard/hr/Shifts.vue');
const Departments = () => import('../pages/dashboard/hr/Departments.vue');
const Designations = () => import('../pages/dashboard/hr/Designations.vue');
const OrgChartPage = () => import('../pages/dashboard/hr/OrgChartPage.vue');
const Holidays = () => import('../pages/dashboard/hr/Holidays.vue');
const Appreciations = () => import('../pages/dashboard/hr/Appreciations.vue');
const Birthdays = () => import('../pages/dashboard/hr/Birthdays.vue');
const Invoices = () => import('../pages/dashboard/finance/Invoices.vue');
const Expenses = () => import('../pages/dashboard/finance/Expenses.vue');
const Payments = () => import('../pages/dashboard/finance/Payments.vue');
const Estimates = () => import('../pages/dashboard/finance/Estimates.vue');
const Proposals = () => import('../pages/dashboard/finance/Proposals.vue');
const ProposalEditor = () => import('../components/proposals/ProposalEditor.vue');
const BankAccounts = () => import('../pages/dashboard/finance/BankAccounts.vue');
const Payroll = () => import('../pages/dashboard/finance/Payroll.vue');
const Contracts = () => import('../pages/dashboard/contracts/Contracts.vue');
const Tickets = () => import('../pages/dashboard/tickets/Tickets.vue');
const TicketDetail = () => import('../pages/dashboard/tickets/TicketDetail.vue');
const Calendar = () => import('../pages/dashboard/calendar/Calendar.vue');
const Messages = () => import('../pages/dashboard/messages/Messages.vue');
const TDrive = () => import('../pages/dashboard/tdrive/TDrive.vue');
const TDocs = () => import('../pages/dashboard/tdocs/TDocs.vue');
const TDocEditor = () => import('../pages/dashboard/tdocs/TDocEditor.vue');
const TSheet = () => import('../pages/dashboard/tsheet/TSheet.vue');
const TSheetEditor = () => import('../pages/dashboard/tsheet/TSheetEditor.vue');
const Assets = () => import('../pages/dashboard/assets/Assets.vue');
const Notices = () => import('../pages/dashboard/notices/Notices.vue');
const KnowledgeBase = () => import('../pages/dashboard/knowledge/KnowledgeBase.vue');
const Reports = () => import('../pages/dashboard/reports/Reports.vue');
const Clients = () => import('../pages/dashboard/clients/Clients.vue');
const ClientDetail = () => import('../pages/dashboard/clients/ClientDetail.vue');
const Leads = () => import('../pages/dashboard/leads/Leads.vue');
const Settings = () => import('../pages/dashboard/settings/Settings.vue');
const Profile = () => import('../pages/dashboard/profile/Profile.vue');
const Notifications = () => import('../pages/dashboard/notifications/Notifications.vue');
const QrGenerator = () => import('../pages/dashboard/tools/QrGenerator.vue');
const AiAgent = () => import('../pages/dashboard/ai/AiAgent.vue');
const SupportTickets = () => import('../pages/dashboard/support/SupportTickets.vue');

// Super Admin pages
const AdminDashboard = () => import('../pages/admin/Dashboard.vue');
const AdminCompanies = () => import('../pages/admin/Companies.vue');
const AdminCompanyDetails = () => import('../pages/admin/CompanyDetails.vue');
const AdminPackages = () => import('../pages/admin/Packages.vue');
const AdminModules = () => import('../pages/admin/Modules.vue');
const AdminBilling = () => import('../pages/admin/Billing.vue');
const AdminStorage = () => import('../pages/admin/Storage.vue');
const AdminSettings = () => import('../pages/admin/Settings.vue');
const AdminApiIntegrations = () => import('../pages/admin/ApiIntegrations.vue');
const AdminInvoiceTemplates = () => import('../pages/admin/InvoiceTemplates.vue');
const AdminAiManagement = () => import('../pages/admin/AiManagement.vue');
const AdminProfile = () => import('../pages/admin/Profile.vue');
const AdminSupportTickets = () => import('../pages/admin/SupportTickets.vue');
const AdminPromoCodes = () => import('../pages/admin/PromoCodes.vue');
const AdminMigration = () => import('../pages/admin/Migration.vue');

// Client pages
const ClientDashboard = () => import('../pages/client/Dashboard.vue');
const ClientProjects = () => import('../pages/client/Projects.vue');
const ClientInvoices = () => import('../pages/client/Invoices.vue');
const ClientTickets = () => import('../pages/client/Tickets.vue');
const ClientContracts = () => import('../pages/client/Contracts.vue');
const ClientProfile = () => import('../pages/client/Profile.vue');
const ClientTDocs = () => import('../pages/client/TDocs.vue');
const ClientTSheets = () => import('../pages/client/TSheets.vue');
const ClientDocEditor = () => import('../pages/client/ClientDocEditor.vue');
const ClientNotifications = () => import('../pages/client/Notifications.vue');
const ClientProposals = () => import('../pages/client/Proposals.vue');
const ClientProposalDetail = () => import('../pages/client/ProposalDetail.vue');

// Billing onboarding pages
const ChoosePlan = () => import('../pages/billing/ChoosePlan.vue');
const SetupSuccess = () => import('../pages/billing/SetupSuccess.vue');

// Public pages
const LeadForm = () => import('../pages/public/LeadForm.vue');
const TicketForm = () => import('../pages/public/TicketForm.vue');
const PublicInvoice = () => import('../pages/PublicInvoice.vue');
const PublicEstimate = () => import('../pages/PublicEstimate.vue');
const PublicProposal = () => import('../pages/public/ProposalView.vue');
const SharedView = () => import('../pages/public/SharedView.vue');

// Error pages
const NotFound = () => import('../pages/errors/NotFound.vue');
const Forbidden = () => import('../pages/errors/Forbidden.vue');

/*
 * Access meta (rbac/guard.ts):
 *  - `principal`: which session may open the route — 'staff' | 'client' | 'superadmin'
 *    (inherited by children). Never a role name: custom roles are staff (R1).
 *  - staff routes declare `permission` (slug | {anyOf} | {allOf} | {slug, scope})
 *    or `access: 'self'`; a staff route with neither is refused (fail closed).
 *  - rbac/navigation.ts items carry the same requirement as their route
 *    (rbac/__tests__/navigation.test.ts).
 */
export const routes: RouteRecordRaw[] = [
  // Super Admin secure login route (obscure URL for security)
  {
    path: '/tasky-level-up-team-access-high-securitlevel',
    name: 'superadmin-login',
    component: SuperAdminLogin,
    meta: { guest: true },
  },

  /*
   * Account deletion instructions.
   *
   * Google Play requires a page it can link from the store listing that
   * anyone can reach without signing in — the reviewer checking it does
   * not have an account, and neither does someone who has already left
   * the company.
   */
  {
    path: '/delete-account',
    name: 'delete-account',
    component: () => import('@/pages/public/DeleteAccount.vue'),
    meta: { guest: true },
  },

  // Client login (separate standalone page)
  {
    path: '/client-login',
    name: 'client-login',
    component: ClientLogin,
    meta: { guest: true },
  },

  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    meta: { guest: true },
    children: [
      { path: '', name: 'signin', component: SignIn },
      { path: 'login', name: 'login', component: Login },
      { path: 'register', name: 'register', component: Register },
      { path: 'forgot-password', name: 'forgot-password', component: ForgotPassword },
      { path: 'reset-password/:token', name: 'reset-password', component: ResetPassword },
    ],
  },

  // Email verification (authenticated, uses AuthLayout)
  {
    path: '/auth/verify-email',
    component: AuthLayout,
    meta: { requiresAuth: true },
    children: [
      { path: '', name: 'verify-email', component: VerifyEmail },
    ],
  },

  // Company Dashboard routes
  {
    path: '/',
    component: DashboardLayout,
    meta: { requiresAuth: true, principal: 'staff' },
    children: [
      { path: '', name: 'dashboard', component: Dashboard, meta: { permission: 'dashboard.view' } },
      { path: 'advanced-dashboard', name: 'advanced-dashboard', component: AdvancedDashboard, meta: { permission: 'advanced-dashboard.view' } },

      // Workplace
      { path: 'projects', name: 'projects', component: Projects, meta: { permission: 'projects.view' } },
      { path: 'projects/:id', name: 'project-detail', component: ProjectDetail, meta: { permission: 'projects.view' } },
      { path: 'tasks', name: 'tasks', component: Tasks, meta: { permission: 'tasks.view' } },
      { path: 'tasks/:id', name: 'task-detail', component: TaskDetail, meta: { permission: 'tasks.view' } },
      { path: 'contracts', name: 'contracts', component: Contracts, meta: { permission: 'contracts.view' } },

      // HR
      { path: 'employees', name: 'employees', component: Employees, meta: { permission: 'employees.view' } },
      { path: 'employees/:id', name: 'employee-detail', component: EmployeeDetail, meta: { permission: 'employees.view' } },
      { path: 'attendance', name: 'attendance', component: Attendance, meta: { permission: 'attendance.view' } },
      { path: 'leaves', name: 'leaves', component: Leaves, meta: { permission: 'leaves.view' } },
      { path: 'shifts', name: 'shifts', component: Shifts, meta: { permission: 'shifts.view' } },
      { path: 'departments', name: 'departments', component: Departments, meta: { permission: 'departments.view' } },
      { path: 'designations', name: 'designations', component: Designations, meta: { permission: 'designations.view' } },
      { path: 'holidays', name: 'holidays', component: Holidays, meta: { permission: 'holidays.view' } },
      { path: 'appreciations', name: 'appreciations', component: Appreciations, meta: { permission: 'appreciations.view' } },
      { path: 'birthdays', name: 'birthdays', component: Birthdays, meta: { access: 'self' } },

      // Finance
      { path: 'invoices', name: 'invoices', component: Invoices, meta: { permission: 'invoices.view' } },
      { path: 'expenses', name: 'expenses', component: Expenses, meta: { permission: 'expenses.view' } },
      { path: 'payments', name: 'payments', component: Payments, meta: { permission: 'payments.view' } },
      { path: 'estimates', name: 'estimates', component: Estimates, meta: { permission: 'estimates.view' } },
      { path: 'proposals', name: 'proposals', component: Proposals, meta: { permission: 'proposals.view' } },
      { path: 'proposals/:id', name: 'proposal-editor', component: ProposalEditor, meta: { permission: 'proposals.view' } },
      { path: 'bank-accounts', name: 'bank-accounts', component: BankAccounts, meta: { permission: 'bank-accounts.view' } },
      { path: 'payroll', name: 'payroll', component: Payroll, meta: { permission: 'payroll.view' } },

      // Other
      { path: 'tickets', name: 'tickets', component: Tickets, meta: { permission: 'tickets.view' } },
      { path: 'tickets/:id', name: 'ticket-detail', component: TicketDetail, meta: { permission: 'tickets.view' } },
      { path: 'calendar', name: 'calendar', component: Calendar, meta: { permission: 'calendar.view' } },

      { path: 'tdocs', name: 'tdocs', component: TDocs, meta: { permission: 'tdrive.view' } },
      { path: 'tsheet', name: 'tsheet', component: TSheet, meta: { permission: 'tdrive.view' } },
      { path: 'assets', name: 'assets', component: Assets, meta: { permission: 'assets.view' } },
      { path: 'notices', name: 'notices', component: Notices, meta: { permission: 'notices.view' } },
      { path: 'knowledge-base/:articleId?', name: 'knowledge-base', component: KnowledgeBase, meta: { permission: 'knowledge-base.view' } },
      { path: 'reports', name: 'reports', component: Reports, meta: { permission: 'reports.view' } },
      { path: 'clients', name: 'clients', component: Clients, meta: { permission: 'clients.view' } },
      { path: 'clients/:id(\\d+)', name: 'client-detail', component: ClientDetail, meta: { permission: 'clients.view' } },
      { path: 'leads', name: 'leads', component: Leads, meta: { permission: 'leads.view' } },
      { path: 'settings', name: 'settings', component: Settings, meta: { permission: SETTINGS_PERMISSION } },
      // The guard checks the redirect's target; this meta mirrors the Billing nav item (navigation.test).
      { path: 'billing', redirect: { name: 'settings', query: { tab: 'billing' } }, meta: { permission: 'settings.edit' } },
      { path: 'profile', name: 'profile', component: Profile, meta: { access: 'self' } },
      { path: 'notifications', name: 'notifications', component: Notifications, meta: { permission: 'notifications.view' } },
      { path: 'qr-generator', name: 'qr-generator', component: QrGenerator, meta: { permission: 'qr-generator.view' } },
      { path: 'ai-settings', redirect: { name: 'settings', query: { tab: 'ai' } } },
      { path: 'support', name: 'support-tickets', component: SupportTickets, meta: { permission: 'support-tickets.view' } },
    ],
  },

  // Full-page routes (no sidebar/topbar, but authenticated)
  {
    path: '/ai-chatbot',
    name: 'ai-chatbot',
    component: AiAgent,
    meta: { requiresAuth: true, principal: 'staff', permission: 'ai-chatbot.view' },
  },
  {
    path: '/org-chart',
    name: 'org-chart',
    component: OrgChartPage,
    meta: { requiresAuth: true, principal: 'staff', permission: 'designations.view' },
  },
  {
    path: '/messages/:conversationId?',
    name: 'messages',
    component: Messages,
    meta: { requiresAuth: true, principal: 'staff', permission: 'messages.view' },
  },
  {
    path: '/tdrive',
    name: 'tdrive',
    component: TDrive,
    meta: { requiresAuth: true, principal: 'staff', permission: 'tdrive.view' },
  },
  {
    path: '/tdocs/:id',
    name: 'tdoc-editor',
    component: TDocEditor,
    meta: { requiresAuth: true, principal: 'staff', permission: 'tdrive.view' },
  },
  {
    path: '/tsheet/:id',
    name: 'tsheet-editor',
    component: TSheetEditor,
    meta: { requiresAuth: true, principal: 'staff', permission: 'tdrive.view' },
  },

  // Legacy redirect: old notification URLs used /tdrive/tdoc/:id and /tdrive/tsheet/:id
  { path: '/tdrive/tdoc/:id', redirect: to => `/tdocs/${to.params.id}` },
  { path: '/tdrive/tsheet/:id', redirect: to => `/tsheet/${to.params.id}` },

  // Super Admin routes (admin.tasklic.com)
  {
    path: '/admin',
    component: AdminLayout,
    meta: { requiresAuth: true, principal: 'superadmin' },
    children: [
      { path: '', name: 'admin-dashboard', component: AdminDashboard },
      { path: 'companies', name: 'admin-companies', component: AdminCompanies },
      { path: 'companies/:id', name: 'admin-company-details', component: AdminCompanyDetails },
      { path: 'packages', name: 'admin-packages', component: AdminPackages },
      { path: 'modules', name: 'admin-modules', component: AdminModules },
      { path: 'billing', name: 'admin-billing', component: AdminBilling },
      { path: 'storage', name: 'admin-storage', component: AdminStorage },
      { path: 'api-integrations', name: 'admin-api-integrations', component: AdminApiIntegrations },
      { path: 'invoice-templates', name: 'admin-invoice-templates', component: AdminInvoiceTemplates },
      { path: 'ai-management', name: 'admin-ai-management', component: AdminAiManagement },
      { path: 'app-settings', redirect: { name: 'admin-settings', query: { tab: 'app' } } },
      { path: 'settings', name: 'admin-settings', component: AdminSettings },
      { path: 'profile', name: 'admin-profile', component: AdminProfile },
      { path: 'promo-codes', name: 'admin-promo-codes', component: AdminPromoCodes },
      { path: 'support-tickets', name: 'admin-support-tickets', component: AdminSupportTickets },
      { path: 'migration', name: 'admin-migration', component: AdminMigration },
    ],
  },

  // Client routes
  {
    path: '/client',
    component: ClientLayout,
    meta: { requiresAuth: true, principal: 'client' },
    children: [
      { path: '', name: 'client-dashboard', component: ClientDashboard },
      { path: 'projects', name: 'client-projects', component: ClientProjects },
      { path: 'invoices', name: 'client-invoices', component: ClientInvoices },
      { path: 'tickets', name: 'client-tickets', component: ClientTickets },
      { path: 'contracts', name: 'client-contracts', component: ClientContracts },
      { path: 'tdocs', name: 'client-tdocs', component: ClientTDocs },
      { path: 'tsheets', name: 'client-tsheets', component: ClientTSheets },
      { path: 'profile', name: 'client-profile', component: ClientProfile },
      { path: 'notifications', name: 'client-notifications', component: ClientNotifications },
      { path: 'proposals', name: 'client-proposals', component: ClientProposals },
      { path: 'proposals/:id(\\d+)', name: 'client-proposal-detail', component: ClientProposalDetail },
    ],
  },
  // Client doc/sheet editors - full page (outside layout)
  {
    path: '/client/tdocs/:id',
    name: 'client-tdoc-editor',
    component: ClientDocEditor,
    meta: { requiresAuth: true, principal: 'client', docType: 'doc' },
  },
  {
    path: '/client/tsheets/:id',
    name: 'client-tsheet-editor',
    component: ClientDocEditor,
    meta: { requiresAuth: true, principal: 'client', docType: 'sheet' },
  },

  // Billing onboarding (authenticated, standalone pages — no DashboardLayout)
  {
    path: '/billing/choose-plan',
    name: 'choose-plan',
    component: ChoosePlan,
    meta: { requiresAuth: true, billingSetup: true, principal: 'staff', permission: 'settings.edit' },
  },
  {
    path: '/billing/setup-success',
    name: 'setup-success',
    component: SetupSuccess,
    meta: { requiresAuth: true, billingSetup: true, principal: 'staff', permission: 'settings.edit' },
  },

  // Public lead form (no auth required)
  {
    path: '/lead-form/:companySlug/:formSlug',
    name: 'lead-form',
    component: LeadForm,
  },

  // Public ticket form (no auth required)
  {
    path: '/ticket-form/:companySlug/:formSlug',
    name: 'ticket-form',
    component: TicketForm,
  },

  // Public invoice view (no auth required)
  {
    path: '/pay/:token',
    name: 'public-invoice',
    component: PublicInvoice,
  },

  // Public estimate view (no auth required)
  {
    path: '/estimate/:token',
    name: 'public-estimate',
    component: PublicEstimate,
  },

  // Public proposal view (no auth required)
  {
    path: '/p/:token',
    name: 'public-proposal',
    component: PublicProposal,
  },

  // Public share view (no auth required)
  {
    path: '/share/:token',
    name: 'public-share',
    component: SharedView,
  },

  // Error routes
  { path: '/403', name: 'forbidden', component: Forbidden },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFound },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Saved position wins; same-page query-only replaces keep the position.
  scrollBehavior: appScrollBehavior,
});

function isOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** users.id whose legacy (pre-permissionScopes) session the guard already tried to refresh. */
let legacyRefreshTriedFor: number | null = null;

/**
 * Navigation guard. Order: auth init → guest pages → email verification →
 * billing gate → sign-in → principal (staff / client / super-admin) →
 * permission. A refused page goes to the person's landing page on a direct
 * load, or to the 403 page (with ?from=) during in-app navigation. The
 * landing page is always one the person may open, so nothing loops (F23).
 */
export async function navigationGuard(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<true | RouteLocationRaw> {
  const authStore = useAuthStore();
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);
  const isGuestRoute = to.matched.some((record) => record.meta.guest);
  const requiredPrincipal = to.meta.principal;

  // Wait for auth check to complete
  if (!authStore.isInitialized) {
    await authStore.checkAuth();
  }

  // Signed in: guest pages resume a safe ?redirect= deep link, else the landing page
  if (isGuestRoute && authStore.isAuthenticated) {
    return safeRedirect(to.query.redirect) ?? { name: authStore.defaultRoute };
  }

  // Redirect unverified users to verify-email page
  if (authStore.isAuthenticated && authStore.user && authStore.user.emailVerified === false) {
    if (to.name !== 'verify-email') {
      return { name: 'verify-email' };
    }
  }

  // Only redirect to choose-plan when trial has truly EXPIRED (not during trial), and only
  // people who can pay (settings.edit). Other staff keep the app; the API answers for them.
  if (
    authStore.isAuthenticated &&
    authStore.user &&
    authStore.user.emailVerified !== false &&
    authStore.user.billingSetupCompleted === false &&
    authStore.user.subscriptionStatus === 'expired' &&
    !authStore.user.isDemo &&
    authStore.principal === 'staff' &&
    authStore.can('settings.edit') &&
    !to.meta.billingSetup
  ) {
    return { name: 'choose-plan' };
  }

  // Redirect unauthenticated users to their sign-in page, keeping the deep link
  if (requiresAuth && !authStore.isAuthenticated) {
    const loginPrincipal = requiredPrincipal === 'client' ? 'client' : 'staff';
    return { name: signInRouteName(loginPrincipal, window.location.hostname), query: { redirect: to.fullPath } };
  }

  if (!authStore.isAuthenticated) return true;

  // Principal: staff pages for staff (any role, custom slugs included), etc.
  if (requiredPrincipal && authStore.principal !== requiredPrincipal) {
    return authStore.principal ? { name: authStore.defaultRoute } : { name: 'forbidden' };
  }

  // A session cached before permissionScopes existed: confirm with the server first (fail closed)
  if (
    authStore.principal === 'staff' &&
    authStore.hasLegacyPermissions &&
    authStore.lastPermissionsCheckAt === null &&
    requirementOf(to.meta) &&
    isOnline() &&
    legacyRefreshTriedFor !== authStore.user?.id
  ) {
    legacyRefreshTriedFor = authStore.user?.id ?? null;
    await authStore.refreshPermissions({ reason: 'legacy-session' });
  }

  if (!isRouteAllowed(to, authStore.permissionSnapshot)) {
    const landing = authStore.defaultRoute;
    const directLoad = from.matched.length === 0;
    if (directLoad && to.name !== landing) return { name: landing, replace: true };
    return { name: 'forbidden', query: { from: to.fullPath } };
  }

  return true;
}

router.beforeEach(navigationGuard);

export default router;
